import {
    AfterViewInit,
    Component,
    ElementRef,
    ViewChild,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { WorkflowGateway } from '../../../gateways/workflow.gateway';
import type { Workflow } from '../../../models/workflow.model';

/** EntityName / EntityDescription limits (17.3). */
export const WORKFLOW_NAME_MAX = 64;
export const WORKFLOW_DESCRIPTION_MAX = 256;

/** Rejects whitespace-only values (`Validators.required` accepts them). */
const NOT_BLANK = Validators.pattern(/\S/);

/**
 * "New workflow" dialog (17.3): required name (≤ 64) and optional description (≤ 256). Creates the
 * workflow and emits it on 201; on any error it stays open and shows the server's message.
 * Focus starts on the name field, is trapped inside, and Escape cancels; the opener restores
 * focus to its trigger on `cancelled`.
 */
@Component({
    selector: 'app-workflow-create-dialog',
    standalone: true,
    imports: [ReactiveFormsModule],
    template: `
        <div class="backdrop" (click)="cancel()"></div>
        <div
            #dialog
            class="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="workflow-create-title"
            data-testid="workflow-create-dialog"
            (keydown)="onKeydown($event)"
        >
            <h2 id="workflow-create-title">New workflow</h2>
            @if (submitError()) {
                <p class="error" role="alert" data-testid="workflow-create-error">
                    {{ submitError() }}
                </p>
            }
            <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
                <label for="workflow-name">Name</label>
                <input
                    #nameInput
                    id="workflow-name"
                    type="text"
                    formControlName="name"
                    [attr.maxlength]="nameMax"
                    data-testid="workflow-create-name"
                    [attr.aria-invalid]="isInvalid('name')"
                    [attr.aria-describedby]="isInvalid('name') ? 'workflow-name-error' : null"
                />
                @if (isInvalid('name')) {
                    <p id="workflow-name-error" class="field-error">
                        Name is required (up to {{ nameMax }} characters).
                    </p>
                }
                <label for="workflow-description">Description (optional)</label>
                <textarea
                    id="workflow-description"
                    rows="3"
                    formControlName="description"
                    [attr.maxlength]="descriptionMax"
                    data-testid="workflow-create-description"
                    [attr.aria-invalid]="isInvalid('description')"
                    [attr.aria-describedby]="
                        isInvalid('description') ? 'workflow-description-error' : null
                    "
                ></textarea>
                @if (isInvalid('description')) {
                    <p id="workflow-description-error" class="field-error">
                        Description must be {{ descriptionMax }} characters or fewer.
                    </p>
                }
                <div class="actions">
                    <button type="button" class="btn" (click)="cancel()">Cancel</button>
                    <button
                        type="submit"
                        class="btn btn-primary"
                        data-testid="workflow-create-submit"
                        [disabled]="submitting()"
                    >
                        {{ submitting() ? 'Creating…' : 'Create workflow' }}
                    </button>
                </div>
            </form>
        </div>
    `,
    styles: [
        `
            .backdrop {
                position: fixed;
                inset: 0;
                background: hsl(var(--color-text-main) / 0.35);
            }
            .dialog {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: min(90vw, 480px);
                padding: 1.5rem;
                background: hsl(var(--color-bg-card));
                color: hsl(var(--color-text-main));
                border-radius: var(--radius-md);
                box-shadow: var(--shadow-lg);
            }
            h2 {
                margin: 0 0 1rem;
                font-size: 1.25rem;
            }
            form {
                display: flex;
                flex-direction: column;
            }
            label {
                font-weight: 600;
                margin: 0.75rem 0 0.4rem;
            }
            input,
            textarea {
                padding: 0.6rem 0.75rem;
                border: 1px solid hsl(var(--border-color));
                border-radius: var(--radius-sm);
                font: inherit;
                background: hsl(var(--color-bg-card));
                color: inherit;
            }
            input[aria-invalid='true'],
            textarea[aria-invalid='true'] {
                border-color: hsl(var(--color-danger));
            }
            .field-error {
                margin: 0.4rem 0 0;
                font-size: 0.85rem;
                color: hsl(var(--color-danger));
            }
            .error {
                margin: 0 0 0.5rem;
                padding: 0.75rem;
                border-radius: var(--radius-sm);
                background: hsl(var(--color-danger-bg));
                color: hsl(var(--color-danger));
            }
            .actions {
                display: flex;
                justify-content: flex-end;
                gap: 0.5rem;
                margin-top: 1.5rem;
            }
            .btn {
                padding: 0.5rem 0.9rem;
                border-radius: var(--radius-sm);
                border: 1px solid hsl(var(--border-color));
                background: hsl(var(--color-bg-card));
                color: hsl(var(--color-text-main));
                font-size: 0.9rem;
            }
            .btn:disabled {
                opacity: 0.6;
                cursor: default;
            }
            .btn-primary {
                background: hsl(var(--color-primary));
                border-color: hsl(var(--color-primary));
                color: hsl(var(--color-bg-card));
            }
            .btn:focus-visible,
            input:focus-visible,
            textarea:focus-visible {
                outline: 2px solid hsl(var(--color-focus-ring));
                outline-offset: 2px;
            }
        `,
    ],
})
export class WorkflowCreateDialog implements AfterViewInit {
    private readonly gateway = inject(WorkflowGateway);
    private readonly fb = inject(FormBuilder);

    @ViewChild('dialog') private dialog?: ElementRef<HTMLElement>;
    @ViewChild('nameInput') private nameInput?: ElementRef<HTMLInputElement>;

    readonly projectId = input.required<string>();
    readonly created = output<Workflow>();
    readonly cancelled = output<void>();

    readonly nameMax = WORKFLOW_NAME_MAX;
    readonly descriptionMax = WORKFLOW_DESCRIPTION_MAX;
    readonly submitting = signal(false);
    readonly submitError = signal('');

    readonly form = this.fb.nonNullable.group({
        name: ['', [Validators.required, NOT_BLANK, Validators.maxLength(WORKFLOW_NAME_MAX)]],
        description: ['', Validators.maxLength(WORKFLOW_DESCRIPTION_MAX)],
    });

    ngAfterViewInit(): void {
        this.nameInput?.nativeElement.focus();
    }

    isInvalid(control: 'name' | 'description'): boolean {
        const c = this.form.controls[control];
        return c.invalid && (c.touched || c.dirty);
    }

    submit(): void {
        if (this.submitting()) {
            return;
        }
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        const { name, description } = this.form.getRawValue();
        this.submitting.set(true);
        this.submitError.set('');
        this.gateway
            .createWorkflow(this.projectId(), {
                name: name.trim(),
                description: description.trim(),
            })
            .subscribe({
                next: (workflow) => {
                    this.submitting.set(false);
                    this.created.emit(workflow);
                },
                error: (err: Error) => {
                    this.submitting.set(false);
                    this.submitError.set(err.message);
                },
            });
    }

    cancel(): void {
        this.cancelled.emit();
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.cancel();
            return;
        }
        if (event.key === 'Tab') {
            this.trapFocus(event);
        }
    }

    private trapFocus(event: KeyboardEvent): void {
        const host = this.dialog?.nativeElement;
        if (!host) {
            return;
        }
        const focusable = Array.from(
            host.querySelectorAll<HTMLElement>('button, [href], input, select, textarea'),
        ).filter((el) => !(el as HTMLButtonElement).disabled);
        if (!focusable.length) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }
}
